import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { post } from '../utils/apiClient';

function suggestionLabel(key) {
  const labels = {
    soap_note: "Generate SOAP Note?",
    risk_assessment: "Start Risk Note?",
    client_summary: "Create Client Summary?",
    therapist_weekly_reflection: "Reflect on the Week?",
    quick_note: "Jot a Quick Note?",
    pending_notes: "Review Pending Notes?",
    offer_vault_save: "Save to Library?",
    offer_vault_deidentify: "Deidentify Before Saving?",
    ask_vault_pseudonym: "Use Which Pseudonym?",
    onboarding: "Start Onboarding Process?",
    awaiting_template_selection: "Select Note Template",
    awaiting_encounter_selection: "Select Patient/Encounter",
    awaiting_session_content: "Provide Session Details",
    manage_templates: "Manage Note Templates"
    };

  return labels[key] || "Continue to next step";
}

function SuggestedActionButton({ lastMessage, sendMessage }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [requestSent, setRequestSent] = useState(false);
  const [requesting, setRequesting] = useState(false);

  if (!lastMessage || lastMessage.sender !== 'assistant' || !lastMessage.suggested_action) {
    return null;
  }

  const actionKey = lastMessage.suggested_action;

  if (actionKey === 'awaiting_session_content') {
    return null;
  }

  // Navigate to note template settings
  if (actionKey === 'manage_templates') {
    return (
      <div className="mt-2 flex justify-start">
        <button
          onClick={() => navigate('/admin/settings?tab=note_templates')}
          className="bg-blue-100 text-blue-700 px-3 py-1.5 rounded-full hover:bg-blue-200 transition duration-150 ease-in-out text-xs font-medium focus:outline-none focus:ring-1 focus:ring-blue-400 shadow-sm flex items-center leading-normal min-h-[28px]"
          aria-label="Manage Note Templates"
        >
          Add Note Template
        </button>
      </div>
    );
  }

  // Upgrade CTA when AI allowance is exhausted
  if (actionKey === 'trial_quota_exceeded') {
    const hasAdminAccess = user && typeof user === 'object' && user.has_admin_settings_access;

    if (hasAdminAccess) {
      // Users with admin settings access can navigate directly to subscription settings
      return (
        <div className="mt-2 flex justify-start">
          <button
            onClick={() => navigate('/admin/settings?tab=subscription')}
            className="bg-amber-100 text-amber-800 px-3 py-1.5 rounded-full hover:bg-amber-200 transition duration-150 ease-in-out text-xs font-medium focus:outline-none focus:ring-1 focus:ring-amber-400 shadow-sm flex items-center leading-normal min-h-[28px]"
            aria-label="Upgrade to Unlimited AI"
          >
            Upgrade to Unlimited AI
          </button>
        </div>
      );
    }

    // Non-admin: request upgrade from admins
    if (requestSent) {
      return (
        <div className="mt-2 flex justify-start">
          <span className="text-green-700 bg-green-50 px-3 py-1.5 rounded-full text-xs font-medium flex items-center leading-normal min-h-[28px]">
            Your admin has been notified about your request for upgrade
          </span>
        </div>
      );
    }

    return (
      <div className="mt-2 flex justify-start">
        <button
          disabled={requesting}
          onClick={async () => {
            setRequesting(true);
            try {
              const response = await post('/api/ai/request-upgrade', {});
              const res = await response.json();
              if (res && res.status === 'ok') {
                setRequestSent(true);
              }
            } catch {
              // Silently fail — the message already tells them to contact admin
            } finally {
              setRequesting(false);
            }
          }}
          className="bg-amber-100 text-amber-800 px-3 py-1.5 rounded-full hover:bg-amber-200 transition duration-150 ease-in-out text-xs font-medium focus:outline-none focus:ring-1 focus:ring-amber-400 shadow-sm flex items-center leading-normal min-h-[28px] disabled:opacity-50"
          aria-label="Request AI Upgrade"
        >
          {requesting ? 'Sending...' : 'Request AI Upgrade'}
        </button>
      </div>
    );
  }

  const label = suggestionLabel(actionKey);

  // If we don't recognize the label, don't show the button
  if (label === "Continue to next step") return null;

  const actionText = `yes, ${actionKey.replaceAll("_", " ")}`;

  return (
    <div className="mt-2 flex justify-start">
      <button
        onClick={() => sendMessage(actionText)}
        className="bg-blue-100 text-blue-700 px-3 py-1.5 rounded-full hover:bg-blue-200 transition duration-150 ease-in-out text-xs font-medium focus:outline-none focus:ring-1 focus:ring-blue-400 shadow-sm flex items-center leading-normal min-h-[28px]"
        aria-label={`Suggested action: ${label}`}
      >
        {label}
      </button>
    </div>
  );
}


export default SuggestedActionButton;
